import { Trans } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { useShareIssueSuspenseQuery } from './ShareIssue.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  GqlBoardsStatementOption,
  GqlCompany,
  GqlMeeting,
  GqlMeetingConcern,
  GqlMeetingKind,
  GqlSeverity,
  GqlShareIssue,
  GqlShareIssueBoardMember,
  GqlShareIssueValidatorErrors,
  GqlSnrAuthorization,
  GqlShareIssueRegistration,
  GqlShareIssueParticipant,
  GqlSirValidatorErrors,
  GqlCurrency,
} from '../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/Button';
import { Divider } from '@startuptools/ui/Divider';
import { NavStepper } from '../../components/steppers/nav-stepper/NavStepper';
import { NavStepperStep } from '../../components/steppers/nav-stepper/NavStepperStep';
import { SiStepMeeting } from './component/steps/StepMeeting';
import { DisplayDocument } from '@startuptools/business-ui/document/Document';
import { SiStepBoardStatement } from './component/steps/StepBoardStatement';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { SiStepAuditorStatement } from './component/steps/StepAuditorStatement';
import { SiStepSplit } from './component/steps/StepSplit';
import { SiStepNewShares } from './component/steps/StepNewShares';
import { SiStepShareClasses } from './component/steps/StepShareClasses';
import { SiStepShareLimits } from './component/steps/StepShareLimits';
import { SiStepPurpose } from './component/steps/StepPurpose';
import { SiStepLanguages } from './component/steps/StepLanguage';
import { SiStepSubscriptionDate } from './component/steps/StepSubscriptionDate';
import { SiStepPaymentMethods } from './component/steps/StepPaymentMethods';
import { SiStepPayByOffset } from './component/steps/StepPayByOffset';
import { SiStepPayByInKind } from './component/steps/StepPayByInKind';
import { SiStepParticipants } from './component/steps/StepParticipants';
import { SiStepRegForm } from './component/steps/StepRegForm';
import { SiStepPayment } from './component/steps/StepPayment';
import { SiStepDocuments } from './component/steps/StepDocuments';
import { SiStepPaymentStatus } from './component/steps/StepPaymentStatus';
import { SiStepExchangeRate } from './component/steps/StepExchangeRate';
import { siShowDocumentsLink } from '../../components/Links';
import { Link, Route, Routes } from 'react-router-dom';
import { useNavigate } from '../../components/Navigate';

export const ShareIssueEditRoutes = ({ shareIssueId }: { shareIssueId: string }) => {
  return (
    <Routes>
      <Route path="" element={<ShareIssueEditNgWrap shareIssueId={shareIssueId} />}>
        <Route path="*" element={<ShareIssueEditNgWrap shareIssueId={shareIssueId} />} />
      </Route>
    </Routes>
  );
};

export const ShareIssueEditNgWrap = ({ shareIssueId }: { shareIssueId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { company, shareIssue, companySubscription, shareIssueErrors },
  } = useShareIssueSuspenseQuery({ variables: { companyId, shareIssueId }, fetchPolicy: 'cache-and-network' });
  const hasActiveSubscription = companySubscription?.isActive ?? false;

  return (
    <ShareIssueEdit
      company={company}
      shareIssue={shareIssue}
      hasActiveSubscription={hasActiveSubscription}
      errors={shareIssueErrors}
    />
  );
};

export const ShareIssueEdit = ({
  company,
  shareIssue,
  hasActiveSubscription,
  errors,
}: {
  company: Pick<GqlCompany, 'id' | 'organizationNumber' | 'name' | 'shareClasses' | 'sharesTotal' | 'sharesMax'>;
  shareIssue: Pick<
    GqlShareIssue,
    | 'id'
    | 'description'
    | 'secondaryLang'
    | 'companyId'
    | 'subscriptionStartDate'
    | 'boardsStatementOption'
    | 'boardsStatementEn'
    | 'boardsStatementDate'
    | 'boardsStatementSv'
    | 'paymentKindNonCash'
    | 'paymentKindOffset'
    | 'currency'
    | 'shareClasses'
    | 'requiresShareLimitChange'
    | 'purpose'
    | 'preferentialRights'
    | 'paymentKindCash'
    | 'paymentKindNonCash'
    | 'paymentKindOffset'
    | 'paymentMethodsCount'
  > & {
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id' | 'appendixNumber'> & {
          meeting: Pick<GqlMeeting, 'id' | 'kind' | 'number' | 'heldKind' | 'startTime' | 'usesStepper'> & {
            latestMinutesDocument?: DisplayDocument | null;
          };
        })
      | null;
    snrAuthorization?: Pick<
      GqlSnrAuthorization,
      'id' | 'noBoardStatement' | 'registrationDate' | 'filingNumber' | 'filingYear' | 'auths'
    > | null;
    boardMembers: (Pick<GqlShareIssueBoardMember, 'id'> & { identity: DisplayIdentity })[];
    shareIssueRegistrations: (Pick<GqlShareIssueRegistration, 'id' | 'regFormPaymentThroughBolago' | 'description'> & {
      validatorErrors: GqlSirValidatorErrors;
      participants: (Pick<
        GqlShareIssueParticipant,
        'id' | 'paymentKind' | 'paymentStatus' | 'numberOfShares' | 'shareClass'
      > & {
        identity: DisplayIdentity;
        proxy?: DisplayIdentity | null;
      })[];
    })[];
  };
  hasActiveSubscription: boolean;
  errors: GqlShareIssueValidatorErrors;
}) => {
  const { link } = useNavigate();
  const meeting = shareIssue.meetingConcern?.meeting;

  const shareIssueRegistration = shareIssue.shareIssueRegistrations.at(0)!;
  const sirErrors = shareIssueRegistration.validatorErrors;

  const editDone =
    ((errors.boardsStatement?.completed && !errors.boardsStatement?.hasError) ?? false) &&
    ((errors.decision?.completed && !errors.decision?.hasError) ?? false) &&
    ((errors.language?.completed && !errors.language?.hasError) ?? false) &&
    ((errors.payByOffset?.completed && !errors.payByOffset?.hasError) ?? false) &&
    ((errors.preferentialRights?.completed && !errors.preferentialRights?.hasError) ?? false) &&
    ((errors.subscriptionDate?.completed && !errors.subscriptionDate?.hasError) ?? false) &&
    ((errors.newShares?.completed && !errors.newShares?.hasError) ?? false) &&
    ((errors.paymentKind?.completed && !errors.paymentKind?.hasError) ?? false) &&
    ((errors.shareClasses?.completed && !errors.shareClasses?.hasError) ?? false) &&
    ((sirErrors.currencyExchangeRate?.completed && !sirErrors.currencyExchangeRate?.hasError) ?? false);

  return (
    <Stack gap={3}>
      <Stack direction="row" gap={3} alignItems="center">
        <Typography level="h3">
          <Trans id='Share Issue "{description}"' values={{ description: shareIssue.description }} />
        </Typography>
        <Divider orientation="vertical" />
        <Tooltip title={!editDone ? <Trans id="Finish all steps before going to the overview" /> : undefined}>
          <span>
            <Button variant="outlined" disabled={!editDone} component={Link} to={link(siShowDocumentsLink(shareIssue))}>
              <Trans id="Go to Overview" />
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <NavStepper path="*" onStepSaved={() => null}>
        <NavStepperStep path="decision" label={<Trans id="Decision" />} validatorStatus={errors.decision}>
          <SiStepMeeting
            meeting={meeting}
            shareIssue={shareIssue}
            snrAuth={shareIssue.snrAuthorization}
            hasActiveSubscription={hasActiveSubscription}
          />
        </NavStepperStep>

        <NavStepperStep path="language" label={<Trans id="Language" />} validatorStatus={errors.language}>
          <SiStepLanguages shareIssue={shareIssue} />
        </NavStepperStep>

        {meeting?.kind !== GqlMeetingKind.ShareholdersMeeting && (
          <NavStepperStep
            path="boards-statement"
            label={<Trans id="Boards Statement" />}
            validatorStatus={errors.boardsStatement}
          >
            <SiStepBoardStatement shareIssue={shareIssue} />
          </NavStepperStep>
        )}

        {shareIssue.boardsStatementOption === GqlBoardsStatementOption.Produce && (
          <NavStepperStep
            path="auditors-statement"
            label={<Trans id="Auditors Statement" />}
            validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
          >
            <SiStepAuditorStatement shareIssue={shareIssue} />
          </NavStepperStep>
        )}

        <NavStepperStep
          path="split"
          label={<Trans id="Split" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <SiStepSplit shareIssue={shareIssue} />
        </NavStepperStep>

        <NavStepperStep
          path="shares"
          label={<Trans id="How much should be raised?" />}
          validatorStatus={errors.newShares}
        >
          <SiStepNewShares shareIssue={shareIssue} />
        </NavStepperStep>

        <NavStepperStep path="share-classes" label={<Trans id="Share Classes" />} validatorStatus={errors.shareClasses}>
          <SiStepShareClasses company={company} shareIssue={shareIssue} />
        </NavStepperStep>

        <NavStepperStep
          path="share-limits"
          label={<Trans id="Change of share limits" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <SiStepShareLimits company={company} shareIssue={shareIssue} />
        </NavStepperStep>

        <NavStepperStep
          path="purpose"
          label={<Trans id="Purpose" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <SiStepPurpose shareIssue={shareIssue} />
        </NavStepperStep>

        <NavStepperStep path="dates" label={<Trans id="Subscription Date" />} validatorStatus={errors.subscriptionDate}>
          <SiStepSubscriptionDate shareIssue={shareIssue} />
        </NavStepperStep>

        <NavStepperStep
          path="payment-methods"
          label={<Trans id="Payment Methods" />}
          validatorStatus={errors.paymentKind}
        >
          <SiStepPaymentMethods shareIssue={shareIssue} />
        </NavStepperStep>

        {shareIssue.paymentKindOffset && (
          <NavStepperStep path="pay-by-offset" label={<Trans id="Offset" />} validatorStatus={errors.payByOffset}>
            <SiStepPayByOffset shareIssue={shareIssue} shareIssueRegistration={shareIssueRegistration} />
          </NavStepperStep>
        )}

        {shareIssue.paymentKindNonCash && (
          <NavStepperStep path="pay-by-in-kind" label={<Trans id="In-Kind" />} validatorStatus={errors.nonCash}>
            <SiStepPayByInKind
              company={company}
              shareIssue={shareIssue}
              shareIssueRegistration={shareIssueRegistration}
            />
          </NavStepperStep>
        )}

        <NavStepperStep
          path="participants"
          label={<Trans id="Who is subscribing for shares?" />}
          validatorStatus={sirErrors.participants}
        >
          <SiStepParticipants shareIssue={shareIssue} shareIssueRegistration={shareIssueRegistration} />{' '}
        </NavStepperStep>

        <NavStepperStep
          path="registration-form"
          label={<Trans id="Registration Form" />}
          validatorStatus={sirErrors.signsRegForm}
        >
          <SiStepRegForm company={company} shareIssueRegistration={shareIssueRegistration} />
        </NavStepperStep>

        {shareIssue.paymentKindCash && (
          <NavStepperStep
            path="payment-account"
            label={<Trans id="Payment Account" />}
            validatorStatus={sirErrors.payment}
          >
            <SiStepPayment company={company} shareIssue={shareIssue} shareIssueRegistration={shareIssueRegistration} />
          </NavStepperStep>
        )}

        {shareIssue.currency !== GqlCurrency.Sek && (
          <NavStepperStep
            path="exchange-rate"
            label={<Trans id="Exchange Rate" />}
            validatorStatus={sirErrors.currencyExchangeRate}
          >
            <SiStepExchangeRate shareIssue={shareIssue} shareIssueRegistration={shareIssueRegistration} />
          </NavStepperStep>
        )}

        <NavStepperStep
          path="documents"
          label={<Trans id="Documents" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <SiStepDocuments shareIssue={shareIssue} shareIssueRegistration={shareIssueRegistration} />
        </NavStepperStep>

        <NavStepperStep
          path="payment-status"
          label={<Trans id="Payment Status" />}
          validatorStatus={sirErrors.paymentStatus}
        >
          <SiStepPaymentStatus
            shareIssue={shareIssue}
            sips={shareIssueRegistration.participants}
            sirId={shareIssueRegistration.id}
          />
        </NavStepperStep>
      </NavStepper>
    </Stack>
  );
};
